import { GUIDE_SEARCH_STRINGS } from "./designStrings";
import {
  escapeHtml,
  GUIDE_MARK_STYLE,
  guideSearchAllRe,
  guideSearchGroups,
  guideSearchSnippet,
  normText,
} from "./guideSearchMatcher";

// User Guide search (Monday 13300893078), ported from Claude Design CMP
// Dashboard (19) guide-search.js. The guide page is a same-origin iframe with
// no scripts, so the dashboard runs this code against the iframe document:
//  - Contents: the design's own search box ([data-guide-search]) shows block
//    hits from the backend in [data-guide-results];
//  - an opened page: matching words highlighted (current #E1A731, others
//    #FCE9BF), the hit block outlined and scrolled to, and a floating bar
//    "Search: q", "i of N", Previous match / Next match / Clear search.
// The version stamp and footer are never highlighted; the bar and marks are
// hidden in print.

export type GuideBlockSearchResult = {
  need: number;
  total: number;
  pages: { slug: string; title: string; hitCount: number; hits: { text: string; heading: string }[] }[];
};

export type GuideHit = { slug: string; q: string; key: string; need: number; t: number };

export const GUIDE_HIT_KEY = "cmpGuideHit";
const HIT_MAX_AGE_MS = 15000;
const SEL = 'h2,h3,h4,p,li,tr,div[style*="break-inside:avoid"],div[style*="border-left:3px"]';

export const storeGuideHit = (hit: GuideHit, storage: Pick<Storage, "setItem"> | null = safeSession()) => {
  try {
    storage?.setItem(GUIDE_HIT_KEY, JSON.stringify(hit));
  } catch {
    // Private mode: the page still opens, without the highlight.
  }
};

/** The pending hit for this page (opened within 15 seconds); removed 4 seconds later like the design. */
export const takeGuideHit = (slug: string, now = Date.now(), storage: Pick<Storage, "getItem" | "removeItem"> | null = safeSession()): GuideHit | null => {
  let hit: GuideHit | null = null;
  try {
    hit = JSON.parse(storage?.getItem(GUIDE_HIT_KEY) || "null") as GuideHit | null;
  } catch {
    hit = null;
  }
  if (!hit || hit.slug !== slug || now - hit.t > HIT_MAX_AGE_MS) return null;
  window.setTimeout(() => {
    try {
      storage?.removeItem(GUIDE_HIT_KEY);
    } catch {
      // ignore
    }
  }, 4000);
  return hit;
};

function safeSession(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export const guideBlocksOf = (root: Element): Element[] =>
  Array.from(root.querySelectorAll(SEL)).filter((el) => {
    if (el.closest('[slot="footer"],[data-guide-toolbar],[data-guide-search-wrap]')) return false;
    if (el.querySelector(SEL)) return false;
    return normText(el.textContent).length > 2;
  });

/* ---------- Contents page: results ---------- */

export const renderGuideResultsHtml = (q: string, result: GuideBlockSearchResult): string => {
  const re = guideSearchAllRe(guideSearchGroups(q));
  let out = "";
  result.pages.forEach((page, pi) => {
    const href = `#guide=${escapeHtml(page.slug)}`;
    out +=
      '<div style="padding:12px 0;border-bottom:1px solid #ECECEC;display:flex;flex-direction:column;gap:6px;">' +
      `<a href="${href}" data-gs-page="${pi}" style="font-size:15px;font-weight:600;color:#0A0A0A;text-decoration:none;">${guideSearchSnippet(page.title, re)}</a>`;
    page.hits.forEach((hit, hi) => {
      out +=
        `<a href="${href}" data-gs-page="${pi}" data-gs-hit="${hi}" style="display:flex;flex-direction:column;gap:2px;padding:6px 10px;border-radius:8px;background:#F7F7F8;color:#0A0A0A;text-decoration:none;font-size:13px;line-height:1.5;">` +
        (hit.heading && hit.heading !== hit.text ? `<span style="font-size:12px;font-weight:600;color:#B7791F;">${escapeHtml(hit.heading)}</span>` : "") +
        `<span>${guideSearchSnippet(hit.text, re)}</span></a>`;
    });
    if (page.hitCount > 3) {
      out += `<a href="${href}" data-gs-page="${pi}" style="font-size:12px;color:#727272;">${GUIDE_SEARCH_STRINGS.moreOnPage(page.hitCount - 3)}</a>`;
    }
    out += "</div>";
  });
  return `<div style="font-size:12px;color:#727272;padding:4px 2px 0;">${escapeHtml(GUIDE_SEARCH_STRINGS.summary(result.total, result.pages.length))}</div>${out}`;
};

const toggleToc = (doc: Document, show: boolean) => {
  doc.querySelectorAll<HTMLElement>('[id^="sec-"]').forEach((sec) => {
    if (sec.id === "sec-chamber") {
      const head = sec.firstElementChild as HTMLElement | null;
      if (head) head.style.display = show ? "" : "none";
      const rows = sec.lastElementChild as HTMLElement | null;
      if (rows && !rows.hasAttribute("data-guide-search-wrap")) rows.style.display = show ? "" : "none";
    } else sec.style.display = show ? "" : "none";
  });
};

/** Wire the design's Contents search box; returns a cleanup function. */
export const attachContentsSearch = (
  doc: Document,
  {
    search,
    open,
  }: {
    search: (q: string) => Promise<GuideBlockSearchResult>;
    open: (hit: Omit<GuideHit, "t">) => void;
  },
): (() => void) => {
  const input = doc.querySelector<HTMLInputElement>("[data-guide-search]");
  if (!input) return () => undefined;
  let timer: number | undefined;
  let last: { q: string; result: GuideBlockSearchResult } | null = null;
  let disposed = false;

  const render = async (): Promise<{ q: string; result: GuideBlockSearchResult } | null> => {
    const q = input.value.trim();
    const box = doc.querySelector<HTMLElement>("[data-guide-results]");
    const noMatch = doc.querySelector<HTMLElement>("[data-guide-nomatch]");
    if (!box) return null;
    if (q.length < 2) {
      box.innerHTML = "";
      box.style.display = "none";
      if (noMatch) noMatch.style.display = "none";
      toggleToc(doc, true);
      last = null;
      return null;
    }
    toggleToc(doc, false);
    if (!last || last.q !== q) {
      box.style.display = "block";
      box.innerHTML = `<div style="font-size:13px;color:#727272;padding:6px 2px;">${GUIDE_SEARCH_STRINGS.searching}</div>`;
      let result: GuideBlockSearchResult;
      try {
        result = await search(q);
      } catch {
        // The search did not run, so do not show the "No matches" text and do
        // not keep this query: the next input searches again.
        if (disposed || input.value.trim() !== q) return null;
        if (noMatch) noMatch.style.display = "none";
        box.style.display = "block";
        box.innerHTML = `<div role="alert" style="font-size:13px;color:#727272;padding:6px 2px;">${escapeHtml(GUIDE_SEARCH_STRINGS.searchFailed)}</div>`;
        last = null;
        return null;
      }
      if (disposed || input.value.trim() !== q) return null;
      last = { q, result };
    }
    const current = last;
    if (noMatch) noMatch.style.display = current.result.pages.length ? "none" : "block";
    if (!current.result.pages.length) {
      box.innerHTML = "";
      box.style.display = "none";
      return current;
    }
    box.style.display = "block";
    box.innerHTML = renderGuideResultsHtml(q, current.result);
    return current;
  };

  const openFrom = (rendered: { q: string; result: GuideBlockSearchResult }, pageIndex: number, hitIndex: number | null) => {
    const page = rendered.result.pages[pageIndex];
    if (!page) return;
    const hit = hitIndex !== null ? page.hits[hitIndex] : page.hits[0];
    open({ slug: page.slug, q: rendered.q, key: (hit ? hit.text : "").slice(0, 80), need: rendered.result.need });
  };

  const onInput = (event: Event) => {
    if (event.target !== input) return;
    window.clearTimeout(timer);
    timer = window.setTimeout(() => void render(), 180);
  };
  const onKeyDown = (event: KeyboardEvent) => {
    if (event.target !== input || event.key !== "Enter") return;
    event.preventDefault();
    window.clearTimeout(timer);
    void render().then((rendered) => {
      if (rendered && rendered.result.pages.length) openFrom(rendered, 0, null);
    });
  };
  const onClick = (event: MouseEvent) => {
    const target = event.target as Element | null;
    const link = target && target.closest ? target.closest("[data-gs-page]") : null;
    if (!link || !last) return;
    event.preventDefault();
    event.stopPropagation();
    const hit = link.getAttribute("data-gs-hit");
    openFrom(last, Number(link.getAttribute("data-gs-page")), hit !== null ? Number(hit) : null);
  };

  doc.addEventListener("input", onInput);
  doc.addEventListener("keydown", onKeyDown);
  doc.addEventListener("click", onClick, true);
  return () => {
    disposed = true;
    window.clearTimeout(timer);
    doc.removeEventListener("input", onInput);
    doc.removeEventListener("keydown", onKeyDown);
    doc.removeEventListener("click", onClick, true);
  };
};

/* ---------- Opened guide page: highlight + scroll ---------- */

const barButton = (label: string, svg: string) =>
  `<button type="button" data-gs-act="${label}" aria-label="${label}" style="width:30px;height:30px;border:1px solid #ECECEC;border-radius:8px;background:#fff;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;padding:0;"><svg viewBox="0 0 24 24" fill="none" stroke="#0A0A0A" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="width:15px;height:15px;pointer-events:none;">${svg}</svg></button>`;

export type GuideHighlight = { marks: HTMLElement[]; current: () => number; next: () => void; previous: () => void; clear: () => void };

export const applyGuideHit = (doc: Document, hit: Pick<GuideHit, "q" | "key" | "need">): GuideHighlight | null => {
  const root = doc.querySelector("[data-guide-page]") || doc.body;
  const groups = guideSearchGroups(hit.q);
  if (!root || !groups.length) return null;
  const re = guideSearchAllRe(groups);
  const key = normText(hit.key).slice(0, 80);
  const blocks = guideBlocksOf(root);
  const need = hit.need || groups.length;
  const okBlocks = blocks.filter((block) => {
    const text = normText(block.textContent);
    return groups.filter((group) => group.re.test(text)).length >= need;
  });
  const target = key ? blocks.find((block) => normText(block.textContent).slice(0, 80) === key) || null : null;
  const view = doc.defaultView || window;
  const walker = doc.createTreeWalker(root, 4 /* NodeFilter.SHOW_TEXT */, {
    acceptNode: (node: Node) => {
      const parent = node.parentElement;
      if (!parent || parent.closest('[slot="footer"],[data-guide-version],script,style,mark')) return 2; /* REJECT */
      if (!okBlocks.some((block) => block.contains(parent))) return 2;
      re.lastIndex = 0;
      return re.test(node.nodeValue || "") ? 1 /* ACCEPT */ : 2;
    },
  });
  const nodes: Text[] = [];
  while (walker.nextNode()) nodes.push(walker.currentNode as Text);
  const marks: HTMLElement[] = [];
  nodes.forEach((node) => {
    const value = node.nodeValue || "";
    const fragment = doc.createDocumentFragment();
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    re.lastIndex = 0;
    while ((match = re.exec(value))) {
      if (match.index > lastIndex) fragment.appendChild(doc.createTextNode(value.slice(lastIndex, match.index)));
      const mark = doc.createElement("mark");
      mark.setAttribute("data-gs", "");
      mark.textContent = match[0];
      mark.style.cssText = GUIDE_MARK_STYLE;
      fragment.appendChild(mark);
      marks.push(mark);
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < value.length) fragment.appendChild(doc.createTextNode(value.slice(lastIndex)));
    node.parentNode?.replaceChild(fragment, node);
  });
  if (!marks.length) return null;

  if (!doc.getElementById("gs-print-css")) {
    const style = doc.createElement("style");
    style.id = "gs-print-css";
    style.textContent = "@media print{[data-gs-bar]{display:none!important}mark[data-gs]{background:none!important}}";
    doc.head.appendChild(style);
  }
  const toolbar = doc.querySelector<HTMLElement>("[data-guide-toolbar]");
  let bar: HTMLElement | null = doc.createElement("div");
  bar.setAttribute("data-gs-bar", "");
  bar.style.cssText = `position:fixed;top:${(toolbar ? toolbar.offsetHeight : 52) + 10}px;right:16px;z-index:60;display:flex;align-items:center;gap:8px;background:#fff;border:1px solid #ECECEC;border-radius:12px;box-shadow:0 4px 16px rgba(0,0,0,.08);padding:8px 10px 8px 14px;font-family:'DM Sans',sans-serif;font-size:13px;color:#0A0A0A;max-width:calc(100vw - 32px);`;
  bar.innerHTML =
    `<span style="min-width:0;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;max-width:260px;" title="${escapeHtml(hit.q).replace(/"/g, "&quot;")}">${GUIDE_SEARCH_STRINGS.barSearch}<strong style="font-weight:600;">${escapeHtml(hit.q)}</strong></span><span data-gs-count style="color:#727272;white-space:nowrap;"></span>` +
    barButton(GUIDE_SEARCH_STRINGS.previousMatch, '<path d="m18 15-6-6-6 6"></path>') +
    barButton(GUIDE_SEARCH_STRINGS.nextMatch, '<path d="m6 9 6 6 6-6"></path>') +
    barButton(GUIDE_SEARCH_STRINGS.clearSearch, '<path d="M18 6 6 18M6 6l12 12"></path>');

  let current = 0;
  const setCurrent = (index: number) => {
    if (!marks.length) return;
    current = (index + marks.length) % marks.length;
    marks.forEach((mark, k) => {
      mark.style.background = k === current ? "#E1A731" : "#FCE9BF";
    });
    const rect = marks[current].getBoundingClientRect();
    const offset = (toolbar ? toolbar.offsetHeight : 0) + 70;
    const top = Math.max(0, rect.top + (view.scrollY || 0) - offset);
    const scroller = doc.scrollingElement || doc.documentElement;
    scroller.scrollTop = top;
    if (Math.abs((view.scrollY || 0) - top) > 2 && typeof view.scrollTo === "function") {
      try {
        view.scrollTo(0, top);
      } catch {
        // jsdom has no scrolling
      }
    }
    const count = bar?.querySelector("[data-gs-count]");
    if (count) count.textContent = GUIDE_SEARCH_STRINGS.barCount(current + 1, marks.length);
  };
  const clear = () => {
    marks.forEach((mark) => {
      const parent = mark.parentNode;
      if (!parent) return;
      parent.replaceChild(doc.createTextNode(mark.textContent || ""), mark);
      parent.normalize();
    });
    doc.querySelectorAll<HTMLElement>("[data-gs-block]").forEach((block) => {
      block.style.boxShadow = "";
      block.removeAttribute("data-gs-block");
    });
    marks.length = 0;
    if (bar) {
      bar.remove();
      bar = null;
    }
  };
  bar.addEventListener("click", (event) => {
    const button = (event.target as Element).closest("[data-gs-act]");
    if (!button) return;
    const action = button.getAttribute("data-gs-act");
    if (action === GUIDE_SEARCH_STRINGS.previousMatch) setCurrent(current - 1);
    else if (action === GUIDE_SEARCH_STRINGS.nextMatch) setCurrent(current + 1);
    else clear();
  });
  doc.body.appendChild(bar);

  let start = 0;
  if (target) {
    target.setAttribute("data-gs-block", "");
    (target as HTMLElement).style.boxShadow = "0 0 0 3px #FCE9BF";
    const k = marks.findIndex((mark) => target.contains(mark));
    if (k > -1) start = k;
  }
  setCurrent(start);
  window.setTimeout(() => setCurrent(start), 60);
  return { marks, current: () => current, next: () => setCurrent(current + 1), previous: () => setCurrent(current - 1), clear };
};
